import { useState, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import type { CategoryListItem } from "../../../services/webServices/category/types";
import { getCategorySearch } from "../../../services/webServices/category/get";
import InputFiel from "../../commons/InputFiel";
import { useDebouncedValue } from "../../../hooks/useSearchInput";
 
const PAGE_SIZE = 12;

interface CategorySearchProps {
  onSelect: (
    categoryTitle: string,
    categoryId: string,
    breadcrumbPath?: string
  ) => void;
  onClear?: () => void;
  onRequestClose?: () => void;
  initialValue?: {
    title?: string;
    breadcrump?: string;
  } | null;
  disabled?: boolean;
}

export default function CategoriesSearch({ onSelect, onClear, onRequestClose, initialValue = null, disabled = false }: CategorySearchProps) {

  const initialText = initialValue?.breadcrump || initialValue?.title || "";

  const [searchKey, setSearchKey] = useState(initialText);
  const [selectedBreadcrumb, setSelectedBreadcrumb] = useState(initialText);
  const [results, setResults] = useState<CategoryListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const debouncedSearchTerm = useDebouncedValue(searchKey.trim());

  useEffect(() => {
    if (!debouncedSearchTerm) {
      setResults([]);
      setOffset(0);
      setHasMore(true);
      return;
    }

    const fetchResults = async () => {
      setLoading(true);

      const res = await getCategorySearch({
        params: {
          search: debouncedSearchTerm,
          limit: PAGE_SIZE,
          offset: 0,
          depth: [0, 1],
        },
      });

      if (res?.length) {
        setResults(res);
        setOffset(PAGE_SIZE);
        setHasMore(res.length >= PAGE_SIZE);
      } else {
        setResults([]);
        setHasMore(false);
      }

      setLoading(false);
    };

    fetchResults();
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (initialValue) {
      const value = initialValue.breadcrump || initialValue.title || "";
      setSearchKey(value);
      setSelectedBreadcrumb(value);
    }
  }, [initialValue]);

  const fetchMore = async () => {
    if (!hasMore || loadingMore || disabled) return;

    setLoadingMore(true);

    const res = await getCategorySearch({
      params: {
        search: debouncedSearchTerm,
        limit: PAGE_SIZE,
        offset,
        depth: [0, 1],
      },
    });

    if (res?.length) {
      setResults((prev) => [...prev, ...res]);
      setOffset((prev) => prev + PAGE_SIZE);
      setHasMore(res.length >= PAGE_SIZE);
    } else {
      setHasMore(false);
    }

    setLoadingMore(false);
  };

  return (
    <>
      <div dir="rtl">
        <div className="relative">
          <InputFiel
            as="input"
            size="lg"
            value={searchKey}
            placeholder="دسته‌بندی مورد نظر را جستجو کنید"
            maxLength="unlimited"
            disabled={disabled}
            onChange={(e) => {
              if (!selectedBreadcrumb) {
                setSearchKey(e.target.value);
              }
            }}
          />

          {selectedBreadcrumb && (
            <span className="absolute inset-y-0 right-0 flex items-center mx-2 pointer-events-none">
              <span className="flex items-center gap-1 bg-successVar-light text-successVar px-3 py-1 rounded-md text-xs font-semibold max-w-[220px] pointer-events-auto">
                <span className="truncate">{selectedBreadcrumb}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBreadcrumb("");
                      setSearchKey("");
                      onClear?.();
                    }}
                    className="text-successVar hover:text-red-500"
                  >
                    ✕
                  </button>
                )}
              </span>
            </span>
          )}
        </div>
      </div>

      {searchKey && !selectedBreadcrumb && (
        <div
          id="scrollableDiv"
          className="text-sm border border-gray-200 rounded-lg overflow-y-auto max-h-64 mt-2"
          dir="rtl"
        >
          {loading && results.length === 0 ? (
            <div className="text-xs text-gray-400 text-center py-4">
              در حال جستجو...
            </div>
          ) : results.length ? (
            <InfiniteScroll
              dataLength={results.length}
              next={fetchMore}
              hasMore={hasMore}
              loader={
                <div className="text-xs text-gray-400 text-center py-2">
                  در حال بارگذاری...
                </div>
              }
              endMessage={
                <div className="text-xs text-gray-400 text-center py-2">
                  نتیجه بیشتری وجود ندارد
                </div>
              }
              scrollableTarget="scrollableDiv"
              style={{ overflow: "visible" }}
            >
              {results.map((item) => (
                <div
                  key={item.id}
                  className="flex py-4 items-center justify-between border-b border-gray-200 mx-4"
                >
                  <span className="text-sm text-gray-500">
                    {item.breadcrump}
                  </span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => {
                        onSelect(item.title, item.id.toString(), item.breadcrump);
                        setSelectedBreadcrumb(item.breadcrump || item.title);
                        setSearchKey(item.breadcrump || item.title);
                        onRequestClose?.();
                      }}
                      className="text-sm px-4 py-2 border border-primaryVar text-primaryVar rounded-xl hover:bg-primaryVar hover:text-white transition-colors"
                    >
                      افزودن
                    </button>
                  )}
                </div>
              ))}
            </InfiniteScroll>
          ) : (
            <div className="text-xs text-gray-400 text-center py-4">
              نتیجه‌ای یافت نشد
            </div>
          )}
        </div>
      )}
    </>
  );
};